<script setup>
import { computed, ref, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from './store/index.js'
import { useTutorial } from '@/core/tutorials/index.js'
import ToastContainer from '@/ui/components/common/ToastContainer.vue'
import ConfirmDialog from '@/ui/components/common/ConfirmDialog.vue'
import TutorialOverlay from '@/core/tutorials/components/TutorialOverlay.vue'
import InstallPrompt from '@/ui/components/common/InstallPrompt.vue'
import AccessibilityWidget from '@/ui/components/common/AccessibilityWidget.vue'
import UpdateAvailableModal from '@/ui/components/common/UpdateAvailableModal.vue'
import PopUpCampaignHost from '@/ui/components/campaigns/PopUpCampaignHost.vue'
import logger from '@/core/services/logger.js'
import { useInstallPrompt } from '@/core/composables/useInstallPrompt.js'

const log = logger.createScoped('App')
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const { setUserId } = useTutorial()
const { initListener, showIfEligible } = useInstallPrompt()

// Show install prompt 4s after user logs in
let installTimer = null
watch(
  () => authStore.isAuthenticated,
  (loggedIn) => {
    if (installTimer) {
      clearTimeout(installTimer)
      installTimer = null
    }
    if (loggedIn) {
      installTimer = setTimeout(() => showIfEligible(), 4000)
    }
  },
  { immediate: true }
)

// Show accessibility widget only on public pages (not under /app)
const showAccessibilityWidget = computed(() => !route.path.startsWith('/app'))

// Show loading overlay while auth is resolving
const isLoading = computed(() => authStore.loading)

// Set tutorial user ID when auth state changes
watch(
  () => authStore.user?.uid,
  (uid) => {
    setUserId(uid || 'anonymous')
  },
  { immediate: true }
)

// ===== PWA UPDATE FLOW (explicit-via-modal) =====
// SW lifecycle: our sw.js no longer self-skip-waits on install. A new SW sits
// in 'waiting' until we tell it to activate. Two paths lead to activation:
//   (a) User clicks "עדכן עכשיו" in the modal — we postMessage SKIP_WAITING.
//   (b) Silent fallback: on a fresh page load, if a waiting SW is already
//       there (carried over from a previous session), we skip-wait it before
//       the user ever sees anything.
//
// Detection triggers (all enabled): immediate after register, every 10 min,
// on route change, on tab visibility, on network back online. Each triggers
// registration.update(), which only installs a new SW if /sw.js bytes changed.
const SW_UPDATE_INTERVAL_MS = 10 * 60 * 1000
const SW_URL = '/sw.js'

let refreshing = false
let userRequestedUpdate = false
let swRegistration = null

// Exposed reactive state for the modal
const updateAvailable = ref(false)
const showUpdateModal = ref(false)

function promptForUpdate() {
  if (!updateAvailable.value) return
  showUpdateModal.value = true
}

function dismissUpdate() {
  showUpdateModal.value = false
  // updateAvailable stays true — next route change re-opens the modal
}

function triggerSkipWaiting() {
  const waiting = swRegistration?.waiting
  if (!waiting) return false
  userRequestedUpdate = true
  waiting.postMessage({ type: 'SKIP_WAITING' })
  // We rely on the `controllerchange` event to reload. No safety timer here:
  // a blind timeout-reload causes a loop with the silent-fallback path below
  // when the SW takes longer than the timeout to activate (common on slower
  // machines and when a lot of precache entries need cleanup).
  return true
}

function confirmUpdate() {
  if (!triggerSkipWaiting()) {
    // Nothing waiting (shouldn't normally happen) — just reload.
    window.location.reload()
  }
}

function onNewWorkerInstalled() {
  // A new SW finished installing AND there's an active SW controlling this
  // page (so this isn't a first-ever install). Offer the update.
  updateAvailable.value = true
  showUpdateModal.value = true
}

async function checkForSwUpdate() {
  if (!swRegistration || swRegistration.installing) return
  if ('onLine' in navigator && !navigator.onLine) return
  try {
    const resp = await fetch(SW_URL, {
      cache: 'no-store',
      headers: { 'cache-control': 'no-cache' },
    })
    if (resp?.status === 200) await swRegistration.update()
  } catch {
    // network/CDN hiccup — ignore, next tick will retry
  }
}

onMounted(() => {
  // Initialize PWA install prompt listener (works in all environments)
  initListener()

  if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return

  // Reload ONLY when the controller change was caused by an explicit user
  // action (confirm modal) or our silent-fallback path. This prevents the
  // spurious reload that would otherwise fire on the very first page load
  // when the SW's clients.claim() takes initial control of an uncontrolled
  // tab — which would also flip `refreshing` to true and block the later,
  // intentional update reload.
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (userRequestedUpdate && !refreshing) {
      refreshing = true
      log.info('New SW took control - reloading')
      window.location.reload()
    }
  })

  navigator.serviceWorker.register(SW_URL).then((registration) => {
    swRegistration = registration
    log.info('SW registered')

    // Silent-fallback path: a waiting SW was left over from a previous session.
    // We try this at most ONCE per tab session — guarded by sessionStorage —
    // so if activation fails for any reason, we don't fall into a reload loop.
    // On subsequent loads within the same session we fall through to the modal
    // (via the updatefound→statechange path); a new tab resets the gate.
    if (registration.waiting && navigator.serviceWorker.controller) {
      const gate = 'pwa_silent_skipwait_tried'
      const alreadyTried = sessionStorage.getItem(gate) === '1'
      if (!alreadyTried) {
        sessionStorage.setItem(gate, '1')
        log.info('Waiting SW detected on load - silent auto-update')
        triggerSkipWaiting()
      } else {
        log.info('Waiting SW still present after prior silent attempt - showing modal')
        onNewWorkerInstalled()
      }
    }

    // Watch for a new SW arriving mid-session.
    registration.addEventListener('updatefound', () => {
      const installing = registration.installing
      if (!installing) return
      installing.addEventListener('statechange', () => {
        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
          log.info('New SW installed & waiting - prompting user')
          onNewWorkerInstalled()
        }
      })
    })

    // Detection triggers (1-5):
    checkForSwUpdate() // 1) immediate — covers reopened tabs
    setInterval(checkForSwUpdate, SW_UPDATE_INTERVAL_MS) // 2) every 10 min
    router.afterEach(() => {
      // 3) on route change: check for a new version AND re-open modal if dismissed
      checkForSwUpdate()
      promptForUpdate()
    })
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) checkForSwUpdate() // 4) on tab visible
    })
    window.addEventListener('online', checkForSwUpdate) // 5) on network back online
  })
})
</script>

<template>
  <!-- Loading Overlay -->
  <Transition name="fade">
    <div v-if="isLoading" class="auth-loading-overlay">
      <div class="loading-content">
        <div class="loading-spinner">
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
        </div>
        <div class="loading-text">טוען סביבת עבודה...</div>
        <div class="loading-progress">
          <div class="progress-bar"></div>
        </div>
      </div>
    </div>
  </Transition>

  <!-- Main App -->
  <RouterView />

  <!-- Toast Notifications -->
  <ToastContainer />

  <!-- Global Confirm Dialog -->
  <ConfirmDialog />

  <!-- Tutorial Overlay -->
  <TutorialOverlay />

  <!-- PWA Install Prompt -->
  <InstallPrompt />

  <!-- PWA Update Available Modal -->
  <UpdateAvailableModal
    v-model="showUpdateModal"
    @confirm="confirmUpdate"
    @dismiss="dismissUpdate"
  />

  <!-- Accessibility Widget (public pages only) -->
  <AccessibilityWidget v-if="showAccessibilityWidget" />

  <!-- Pop-up Campaign auto-display -->
  <PopUpCampaignHost />
</template>

<style scoped>
/* Loading Overlay */
.auth-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-lg);
}

/* Spinner with 3 rings */
.loading-spinner {
  position: relative;
  width: 64px;
  height: 64px;
}

.spinner-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 3px solid transparent;
  border-top-color: var(--color-primary);
  animation: spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

.spinner-ring:nth-child(1) {
  animation-delay: -0.45s;
  opacity: 1;
}

.spinner-ring:nth-child(2) {
  inset: 6px;
  animation-delay: -0.3s;
  opacity: 0.8;
  border-top-color: var(--color-primary);
}

.spinner-ring:nth-child(3) {
  inset: 12px;
  animation-delay: -0.15s;
  opacity: 0.6;
  border-top-color: var(--color-primary);
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Loading text with subtle pulse */
.loading-text {
  font-size: var(--font-size-base);
  color: var(--text-secondary);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

/* Progress bar with shimmer effect */
.loading-progress {
  width: 200px;
  height: 3px;
  background: var(--bg-tertiary);
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  width: 40%;
  background: linear-gradient(90deg, transparent, var(--color-primary), transparent);
  animation: shimmer 1.5s ease-in-out infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(350%);
  }
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
